import { useState, useEffect } from 'react';
import api from '../api';

// Helfer
const fmtSatz = (v) => {
  if (v == null || v === '') return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  return n.toFixed(2) + ' €/h';
};
// Fix Datum-Shift (gleiches Muster wie toLocalDateStr in BautagebuchForm/
// Projektpositionen, bewusste Mini-Duplikation): pg liefert DATE-Spalten als
// ISO-Timestamp der LOKALEN Mitternacht in UTC ('…-14T22:00:00.000Z' fuer
// den 15.). Reines substring/slice(0,10) uebernimmt den UTC-Versatz (-1 Tag)
// - stattdessen als lokales Datum interpretieren und YYYY-MM-DD bauen.
function toLocalDateStr(v) {
  if (!v) return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return s.slice(0, 10);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
const fmtDate = (v) => toLocalDateStr(v) || '–';

// ─────────────────────────────────────────────────────────────────────────────
// Stammdaten-Page (A.4.3): pflegt die zwei Stammdaten-Bereiche, die in
// Variante A neu hinzugekommen sind:
//   - Funktions-Rollen (Tabelle rollen)
//   - LV-Stundensaetze pro Baustelle x Rolle (Tabelle lv_stundensaetze)
//
// Interner Mitarbeiter-Stundensatz wird NICHT hier gepflegt - der haengt
// am User-Datensatz und wird in Team.js (A.4.2) bearbeitet.
//
// Routing/Sidebar-Eintrag folgt in A.4.4. Solange A.4.4 noch nicht da ist,
// ist die Page nur ueber manuelle URL erreichbar.
// ─────────────────────────────────────────────────────────────────────────────

export default function Stammdaten() {
  const [rollen, setRollen] = useState([]);
  const [baustellen, setBaustellen] = useState([]);
  const [subunternehmer, setSubunternehmer] = useState([]);

  const loadRollen = () => api.get('/rollen')
    .then(r => setRollen(Array.isArray(r.data) ? r.data : []))
    .catch(() => setRollen([]));
  const loadBaustellen = () => api.get('/baustellen')
    .then(r => setBaustellen(Array.isArray(r.data) ? r.data : []))
    .catch(() => setBaustellen([]));
  // A.7: inkl. archivierte, damit die Section beide Zustände zeigen kann
  // (Muster Rollen: aktive zuerst, inaktive ausgegraut).
  const loadSubunternehmer = () => api.get('/subunternehmer?archiviert=1')
    .then(r => setSubunternehmer(Array.isArray(r.data) ? r.data : []))
    .catch(() => setSubunternehmer([]));

  useEffect(() => { loadRollen(); loadBaustellen(); loadSubunternehmer(); }, []);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Stammdaten</div>
          <div className="page-sub">Funktions-Rollen, LV-Stundensätze pro Baustelle und Subunternehmer</div>
        </div>
      </div>

      <RollenSection rollen={rollen} reload={loadRollen} />

      <div style={{ height: 20 }} />

      <LvSaetzeSection rollen={rollen} baustellen={baustellen} />

      <div style={{ height: 20 }} />

      <SubunternehmerSection subunternehmer={subunternehmer} reload={loadSubunternehmer} />
    </div>
  );
}

// ─── Section: Funktions-Rollen ───────────────────────────────────────────────
function RollenSection({ rollen, reload }) {
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/rollen/${editing.id}`, data);
      } else {
        await api.post('/rollen', data);
      }
      setShowForm(false);
      setEditing(null);
      reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Speichern fehlgeschlagen');
    }
  };

  const toggleAktiv = async (r) => {
    const aktion = r.aktiv ? 'deaktivieren' : 'reaktivieren';
    if (!window.confirm(
      `Rolle "${r.bezeichnung}" ${aktion}?\n` +
      `Bestehende Zuordnungen in Mitarbeitern und Bautagebuch-Stunden bleiben erhalten.`
    )) return;
    try {
      await api.put(`/rollen/${r.id}`, { aktiv: !r.aktiv });
      reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Aktion fehlgeschlagen');
    }
  };

  // Sortierung: aktive zuerst, je Gruppe nach reihenfolge, bezeichnung
  const sorted = [...rollen].sort((a, b) => {
    if (a.aktiv !== b.aktiv) return a.aktiv ? -1 : 1;
    if (a.reihenfolge !== b.reihenfolge) return (a.reihenfolge || 0) - (b.reihenfolge || 0);
    return (a.bezeichnung || '').localeCompare(b.bezeichnung || '');
  });

  const aktivCount = rollen.filter(r => r.aktiv).length;
  const inaktivCount = rollen.length - aktivCount;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">Funktions-Rollen</div>
          <div className="card-sub">{rollen.length} Rollen · {aktivCount} aktiv · {inaktivCount} inaktiv</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing({}); setShowForm(true); }}>+ Neue Rolle</button>
      </div>

      {sorted.length === 0 ? (
        <div className="empty"><div className="empty-icon">📋</div><h3>Keine Rollen</h3><p>Lege die erste Funktions-Rolle an.</p></div>
      ) : (
        <div className="table-wrap table-mobile-stack">
          <table>
            <thead>
              <tr>
                <th>Schlüssel</th><th>Bezeichnung</th><th>Reihenfolge</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.id} style={{ opacity: r.aktiv ? 1 : 0.6 }}>
                  <td data-label="Schlüssel" className="text-mono">{r.schluessel}</td>
                  <td data-label="Bezeichnung" className="td-title" style={{ fontWeight: 500 }}>{r.bezeichnung}</td>
                  <td data-label="Reihenfolge" className="text-mono">{r.reihenfolge ?? 0}</td>
                  <td data-label="Status">{r.aktiv ? <span className="badge badge-green">Aktiv</span> : <span className="badge badge-grey">Inaktiv</span>}</td>
                  <td data-label="" className="td-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(r); setShowForm(true); }} title="Bearbeiten">✏️</button>
                    {r.aktiv ? (
                      <button className="btn btn-danger btn-sm" onClick={() => toggleAktiv(r)} title="Deaktivieren">🗑</button>
                    ) : (
                      <button className="btn btn-secondary btn-sm" onClick={() => toggleAktiv(r)} title="Reaktivieren">↩️</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <RolleForm
          rolle={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function RolleForm({ rolle, onClose, onSave }) {
  const isEdit = !!rolle?.id;
  const [data, setData] = useState({
    schluessel: rolle?.schluessel || '',
    bezeichnung: rolle?.bezeichnung || '',
    reihenfolge: rolle?.reihenfolge != null ? String(rolle.reihenfolge) : '0',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={onClose}>
      <div className="card" style={{ maxWidth: 500, width: '100%', margin: 0, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="card-title" style={{ marginBottom: 14 }}>{isEdit ? 'Rolle bearbeiten' : 'Neue Rolle'}</div>

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Schlüssel (technisch, kleinbuchstaben)</label>
          <input
            className="form-input text-mono"
            value={data.schluessel}
            onChange={e => setData({ ...data, schluessel: e.target.value })}
            placeholder="z. B. monteur, bauleiter"
            maxLength={40}
            required
          />
        </div>

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Bezeichnung</label>
          <input
            className="form-input"
            value={data.bezeichnung}
            onChange={e => setData({ ...data, bezeichnung: e.target.value })}
            placeholder="z. B. Monteur"
            required
          />
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Reihenfolge (Sortierung)</label>
          <input
            className="form-input"
            type="number"
            step="1"
            inputMode="numeric"
            value={data.reihenfolge}
            onChange={e => setData({ ...data, reihenfolge: e.target.value })}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => onSave(data)}>{isEdit ? 'Speichern' : 'Anlegen'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Subunternehmer (A.7) ───────────────────────────────────────────
// CRUD gegen /api/subunternehmer (adminOnly). Soft-Delete via aktiv wie bei
// Rollen: Deaktivieren nimmt den Sub nur aus den Auswahl-Optionen, bestehende
// Stundenzeilen behalten ihre Zuordnung.
function SubunternehmerSection({ subunternehmer, reload }) {
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/subunternehmer/${editing.id}`, { ...data, aktiv: editing.aktiv !== false });
      } else {
        await api.post('/subunternehmer', data);
      }
      setShowForm(false);
      setEditing(null);
      reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Speichern fehlgeschlagen');
    }
  };

  const toggleAktiv = async (s) => {
    const aktion = s.aktiv ? 'archivieren' : 'reaktivieren';
    if (!window.confirm(
      `Subunternehmer "${s.firma}" ${aktion}?\n` +
      `Bestehende Bautagebuch-Stundenzeilen behalten ihre Zuordnung.`
    )) return;
    try {
      if (s.aktiv) {
        await api.delete(`/subunternehmer/${s.id}`);
      } else {
        await api.put(`/subunternehmer/${s.id}`, {
          firma: s.firma, ansprechpartner: s.ansprechpartner, telefon: s.telefon,
          email: s.email, notizen: s.notizen, aktiv: true,
        });
      }
      reload();
    } catch (e) {
      alert(e.response?.data?.error || 'Aktion fehlgeschlagen');
    }
  };

  const sorted = [...subunternehmer].sort((a, b) => {
    if (a.aktiv !== b.aktiv) return a.aktiv ? -1 : 1;
    return (a.firma || '').localeCompare(b.firma || '');
  });
  const aktivCount = subunternehmer.filter(s => s.aktiv).length;
  const inaktivCount = subunternehmer.length - aktivCount;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">Subunternehmer</div>
          <div className="card-sub">
            {subunternehmer.length} Firmen · {aktivCount} aktiv · {inaktivCount} archiviert —
            im Bautagebuch bei externen Stundenzeilen auswählbar
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditing({}); setShowForm(true); }}>+ Neuer Subunternehmer</button>
      </div>

      {sorted.length === 0 ? (
        <div className="empty"><div className="empty-icon">🏢</div><h3>Keine Subunternehmer</h3><p>Lege die erste Firma an — danach ist sie im Bautagebuch auswählbar.</p></div>
      ) : (
        <div className="table-wrap table-mobile-stack">
          <table>
            <thead>
              <tr>
                <th>Firma</th><th>Ansprechpartner</th><th>Kontakt</th><th>Notizen</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => (
                <tr key={s.id} style={{ opacity: s.aktiv ? 1 : 0.6 }}>
                  <td data-label="Firma" className="td-title" style={{ fontWeight: 500 }}>{s.firma}</td>
                  <td data-label="Ansprechpartner">{s.ansprechpartner || '–'}</td>
                  <td data-label="Kontakt" style={{ fontSize: 12 }}>
                    {[s.telefon, s.email].filter(Boolean).join(' · ') || '–'}
                  </td>
                  <td data-label="Notizen" style={{ fontSize: 12 }}>{s.notizen || '–'}</td>
                  <td data-label="Status">{s.aktiv ? <span className="badge badge-green">Aktiv</span> : <span className="badge badge-grey">Archiviert</span>}</td>
                  <td data-label="" className="td-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(s); setShowForm(true); }} title="Bearbeiten">✏️</button>
                    {s.aktiv ? (
                      <button className="btn btn-danger btn-sm" onClick={() => toggleAktiv(s)} title="Archivieren">🗑</button>
                    ) : (
                      <button className="btn btn-secondary btn-sm" onClick={() => toggleAktiv(s)} title="Reaktivieren">↩️</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <SubunternehmerForm
          sub={editing}
          alle={subunternehmer}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function SubunternehmerForm({ sub, alle, onClose, onSave }) {
  const isEdit = !!sub?.id;
  const [data, setData] = useState({
    firma: sub?.firma || '',
    ansprechpartner: sub?.ansprechpartner || '',
    telefon: sub?.telefon || '',
    email: sub?.email || '',
    notizen: sub?.notizen || '',
  });

  // Dubletten-Hinweis (kein harter UNIQUE-Zwang, Muster M1.3): case-insensitiv
  // gegen bestehende Firmen vergleichen, sich selbst beim Edit ausnehmen.
  const firmaNorm = data.firma.trim().toLowerCase();
  const dublette = firmaNorm !== '' && alle.find(s =>
    s.id !== sub?.id && (s.firma || '').trim().toLowerCase() === firmaNorm
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={onClose}>
      <div className="card" style={{ maxWidth: 500, width: '100%', margin: 0, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="card-title" style={{ marginBottom: 14 }}>{isEdit ? 'Subunternehmer bearbeiten' : 'Neuer Subunternehmer'}</div>

        <div className="form-group" style={{ marginBottom: dublette ? 4 : 10 }}>
          <label className="form-label">Firma *</label>
          <input
            className="form-input"
            value={data.firma}
            onChange={e => setData({ ...data, firma: e.target.value })}
            placeholder="z. B. Elektro Fremd GmbH"
            maxLength={200}
            required
          />
        </div>
        {dublette && (
          <div style={{ fontSize: 12, color: 'var(--amber, #b8860b)', marginBottom: 10 }}>
            ⚠ Es existiert bereits ein Subunternehmer „{dublette.firma}"{dublette.aktiv === false ? ' (archiviert)' : ''} — Anlegen ist trotzdem möglich, bitte Dublette vermeiden.
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Ansprechpartner</label>
          <input
            className="form-input"
            value={data.ansprechpartner}
            onChange={e => setData({ ...data, ansprechpartner: e.target.value })}
            maxLength={100}
            placeholder="optional"
          />
        </div>

        <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Telefon</label>
            <input
              className="form-input"
              value={data.telefon}
              onChange={e => setData({ ...data, telefon: e.target.value })}
              maxLength={50}
              placeholder="optional"
            />
          </div>
          <div className="form-group">
            <label className="form-label">E-Mail</label>
            <input
              className="form-input"
              type="email"
              value={data.email}
              onChange={e => setData({ ...data, email: e.target.value })}
              maxLength={100}
              placeholder="optional"
            />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Notizen</label>
          <textarea
            className="form-textarea"
            value={data.notizen}
            onChange={e => setData({ ...data, notizen: e.target.value })}
            placeholder="optional"
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" disabled={!data.firma.trim()} onClick={() => onSave(data)}>{isEdit ? 'Speichern' : 'Anlegen'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Section: LV-Stundensätze pro Baustelle ──────────────────────────────────
function LvSaetzeSection({ rollen, baustellen }) {
  const [baustelleId, setBaustelleId] = useState('');
  const [saetze, setSaetze] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Nur aktive Rollen im Form-Dropdown, defensive Hangover-Logik fuer
  // deaktivierte Bestands-Zuordnungen wird im Form selbst gemacht.
  const rollenAktiv = rollen.filter(r => r.aktiv);

  const loadSaetze = (bId) => {
    if (!bId) { setSaetze([]); return; }
    api.get(`/baustellen/${bId}/lv-stundensaetze`)
      .then(r => setSaetze(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSaetze([]));
  };

  useEffect(() => { loadSaetze(baustelleId); }, [baustelleId]);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/lv-stundensaetze/${editing.id}`, data);
      } else {
        await api.post(`/baustellen/${baustelleId}/lv-stundensaetze`, data);
      }
      setShowForm(false);
      setEditing(null);
      loadSaetze(baustelleId);
    } catch (e) {
      alert(e.response?.data?.error || 'Speichern fehlgeschlagen');
    }
  };

  const del = async (s) => {
    const datumHint = s.gueltig_ab ? ` (gültig ab ${fmtDate(s.gueltig_ab)})` : '';
    if (!window.confirm(
      `LV-Stundensatz "${s.rolle_bezeichnung}: ${fmtSatz(s.satz)}"${datumHint} löschen?\n` +
      `Bereits gebuchte Stunden mit Snapshot dieses Satzes bleiben unverändert.`
    )) return;
    try {
      await api.delete(`/lv-stundensaetze/${s.id}`);
      loadSaetze(baustelleId);
    } catch (e) {
      alert(e.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const baustelleName = baustellen.find(b => String(b.id) === String(baustelleId))?.name || '';

  return (
    <div className="card">
      <div className="card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div className="card-title">LV-Stundensätze</div>
          <div className="card-sub">
            Abrechenbare Stundensätze pro Baustelle × Funktions-Rolle, optional zeitlich gestaffelt
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="form-select"
            value={baustelleId}
            onChange={e => setBaustelleId(e.target.value)}
            style={{ minWidth: 220 }}
          >
            <option value="">— Baustelle wählen —</option>
            {baustellen.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <button
            className="btn btn-primary btn-sm"
            disabled={!baustelleId}
            onClick={() => { setEditing({}); setShowForm(true); }}
            title={baustelleId ? 'Neuen Satz anlegen' : 'Erst Baustelle wählen'}
          >+ Neuer Satz</button>
        </div>
      </div>

      {!baustelleId ? (
        <div className="empty">
          <div className="empty-icon">🏗️</div>
          <h3>Baustelle wählen</h3>
          <p>Oben eine Baustelle auswählen, um die LV-Stundensätze anzuzeigen.</p>
        </div>
      ) : saetze.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">💶</div>
          <h3>Keine LV-Sätze für „{baustelleName}"</h3>
          <p>Lege den ersten LV-Stundensatz für diese Baustelle an.</p>
        </div>
      ) : (
        <div className="table-wrap table-mobile-stack">
          <table>
            <thead>
              <tr>
                <th>Funktion</th><th>Satz</th><th>gültig ab</th><th>Notiz</th><th></th>
              </tr>
            </thead>
            <tbody>
              {saetze.map(s => (
                <tr key={s.id}>
                  <td data-label="Funktion" className="td-title" style={{ fontWeight: 500 }}>{s.rolle_bezeichnung}</td>
                  <td data-label="Satz" className="text-mono">{fmtSatz(s.satz)}</td>
                  <td data-label="gültig ab" className="text-mono">{fmtDate(s.gueltig_ab)}</td>
                  <td data-label="Notiz">{s.notiz || '–'}</td>
                  <td data-label="" className="td-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(s); setShowForm(true); }} title="Bearbeiten">✏️</button>
                    <button className="btn btn-danger btn-sm" onClick={() => del(s)} title="Löschen">🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <LvSatzForm
          satz={editing}
          rollen={rollenAktiv}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function LvSatzForm({ satz, rollen, onClose, onSave }) {
  const isEdit = !!satz?.id;
  const [data, setData] = useState({
    rolle_id: satz?.rolle_id != null ? String(satz.rolle_id) : '',
    satz: satz?.satz != null ? String(satz.satz) : '',
    gueltig_ab: toLocalDateStr(satz?.gueltig_ab),
    notiz: satz?.notiz || '',
  });

  // Bestandsschutz: wenn am Eintrag eine inzwischen deaktivierte Rolle haengt,
  // wird sie defensiv im Dropdown gehalten.
  const currentRolleInList = rollen.some(r => r.id === satz?.rolle_id);
  const dropdownRollen = (!currentRolleInList && satz?.rolle_id != null && satz?.rolle_bezeichnung)
    ? [...rollen, { id: satz.rolle_id, bezeichnung: `${satz.rolle_bezeichnung} (deaktiviert)` }]
    : rollen;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={onClose}>
      <div className="card" style={{ maxWidth: 500, width: '100%', margin: 0, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="card-title" style={{ marginBottom: 14 }}>{isEdit ? 'LV-Satz bearbeiten' : 'Neuer LV-Satz'}</div>

        <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Funktion</label>
            <select
              className="form-select"
              value={data.rolle_id}
              onChange={e => setData({ ...data, rolle_id: e.target.value })}
              required
            >
              <option value="">— wählen —</option>
              {dropdownRollen.map(r => (
                <option key={r.id} value={r.id}>{r.bezeichnung}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Satz (€/h)</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={data.satz}
              onChange={e => setData({ ...data, satz: e.target.value })}
              required
            />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">gültig ab (optional)</label>
          <input
            className="form-input"
            type="date"
            value={data.gueltig_ab}
            onChange={e => setData({ ...data, gueltig_ab: e.target.value })}
            placeholder="leer = sofort gültig"
          />
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Notiz</label>
          <input
            className="form-input"
            value={data.notiz}
            onChange={e => setData({ ...data, notiz: e.target.value })}
            placeholder='z. B. „nach Tarifrunde 2026" — optional'
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => onSave(data)}>{isEdit ? 'Speichern' : 'Anlegen'}</button>
        </div>
      </div>
    </div>
  );
}
